<template>
  <div class="editor-wrapper">
    <div class="editor-frame-container">
      <iframe
        width="100%"
        height="600px"
        title="design-editor"
        id="editorFrame"
        ref="editorFrame"
      ></iframe>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type Ref, watch, inject } from 'vue';
import pinia from '../../core/pinia';
import {
  useConfigStore,
  useEditorStore,
  useLineItemStore,
  useVdpTableStore
} from '../../stores';
import { E_VARIABLE_SURFACE, type ICommandManager, type IProductItems, type ISaveProductResponse, type TDesignEditor, type TVariableItems } from '../../types/design-editor';
import emitter from '../../core/emitter';
import { E_EDITOR_CUSTOM_EVENT } from '../../types/custom-events.enums';
import { InnerVdpDataFormatter } from '../../utils/inner-vdp-data-formatter';
import loadDatasets from '../../utils/load-datasets';
import { arraysHaveSameKeys } from '../../utils/arrays-have-same-keys';
import { E_SCENARIO } from '../../types/line-item.types';


const configStore = useConfigStore(pinia);
const editorStore = useEditorStore(pinia);
const lineItemStore = useLineItemStore(pinia);
const vdpTableStore = useVdpTableStore(pinia);

let checkLoad = false;

const editorFrame: Ref<HTMLIFrameElement | null> = ref(null);
const editorRef: Ref<TDesignEditor | null> = ref(null);
const commandManagerRef: Ref<ICommandManager | null> = ref(null);
const variableItems: Ref<TVariableItems> = ref({ [E_VARIABLE_SURFACE.DEFAULT]: [] });

const iframeApiLoadStatus = inject<Ref<'not' | 'pending' | 'loaded'>>('iframeApiLoadStatus');

const isRestore = computed(() => editorStore.getIsRestore);
const productDefinition = computed(() => {
  const stateId = editorStore.getStateId;
  if (stateId) {
    return [stateId];
  } else {
    return configStore.getProductDefinition;
  }
});
const editorConfig = computed(() => configStore.getEditorConfig);

async function initDesignEditor(editorFrame: HTMLIFrameElement, productDefinition: string[] | string, editorConfig: any) {
  // --de-brand-primary: #FF705E;
  await CustomersCanvas.IframeApi.loadEditor(
    editorFrame,
    productDefinition,
    editorConfig
  ).then(async (editor) => {
    editorRef.value = editor;
    commandManagerRef.value = editor.commandManager as ICommandManager; //.execute("deleteItems", { query: "#id" })
    await saveProduct();
    emitter.emit('EDITOR_LOAD');
  }).catch(error => {
    console.error(
      'The editor failed to load with an exception: ',
      error
    );
  });
}

async function saveProduct() {
  const response = await editorRef.value.saveProduct(productDefinition.value) as ISaveProductResponse;
  console.log('response', response);
  setupDataset();
  editorStore.updateStateId(response.stateId);
  lineItemStore.updateLineItem({
    stateId: [response.stateId]
  });
  await vdpTableStore.putVdpInState();
  emitter.emit(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN_SUCCESS, response.stateId);
  return response;
}

// async function getProofImages() {
//   const response = await editorRef.value.getProofImages();
//   const proofImageUrls = response.proofImageUrls;
//   editorStore.setProofs(proofImageUrls);
//   lineItemStore.updateLineItem({
//     images: proofImageUrls,
//   });
//   return response;
// }

async function setupDataset() {
  const product = await editorRef.value.getProduct();
  variableItems.value = await product.getVariableItems();
  
  const vdpData = InnerVdpDataFormatter({
    vdpDataWithSurface: variableItems.value,
    revers: true,
  });
  
  const currentSurfaceData = vdpData.surfacesData;
  const beforeSurfaceData = vdpTableStore.getSurfacesData;

  if (!arraysHaveSameKeys<Record<string, string>[]>(currentSurfaceData.columnsTypes, beforeSurfaceData?.columnsTypes)) {
    console.log('* UPDATE TABLE! *');
    loadDatasets(vdpData.datasets, vdpData.surfacesData);
  }

  if (hasRowsDataInTable() && isRestore.value) {
    lineItemStore.updateLineItem({
      scenario: E_SCENARIO.VDP,
    });
  }
}

function hasRowsDataInTable() {
  const dataset = editorStore.getDataset;
  return dataset?.some((sheet) => !!sheet?.rows?.length)
}

async function getMappedItems() {
  const product = await editorRef.value.getProduct();
  
  let dirtyItems: Record<string, any>[] = (await product.getProductModel()).getAllItems();
  dirtyItems = dirtyItems?.filter((item) => item._parentContainer._name !== 'Mockup');

  const mappedItems = mapDirtyItems(dirtyItems);
  return mappedItems;
}

async function getProductItems(): Promise<IProductItems> {
  const mappedItems = await getMappedItems();
  
  const placeholderItems = extractItemsByType(mappedItems, "PlaceholderItem");
  const boundedTextItems = extractItemsByType(mappedItems, "BoundedTextItem");
  const plainTextItems = extractItemsByType(mappedItems, "PlainTextItem");

  const productItems: IProductItems = {
    placeholderItems,
    boundedTextItems,
    plainTextItems,
  };

  emitter.emit(E_EDITOR_CUSTOM_EVENT.GET_PRODUCT_ITEMS_SUCCESS, productItems);
  return productItems;
}

function mapDirtyItems(allItems: Record<string, any>[]) {
  const itemHeap: Record<string, any>[] = [];
  function mapper(item: Record<string, any>) {
    const itemGroup = item?.items;
    if (!!itemGroup) {
      const len = itemGroup?.length;
      if (len) {
        for (let i = 0; i < len; i++) {
          let _item = itemGroup.get(i);
          if (!!_item?.items) {
            mapper(_item);
          } else {
            itemHeap.push(_item);
          }
        }
      }
    } else {
      itemHeap.push(item);
    }
  }
  allItems.map(mapper);
  return itemHeap;
}

function extractItemsByType(mappedItems: Record<string, any>[], type: "PlaceholderItem" | "BoundedTextItem" | "PlainTextItem") {
  return mappedItems.filter((item) => item.type === type);
}

async function getItemsGroup() {
  const mappedItems = await getMappedItems();
  
  const groupsSet = new Set(mappedItems.map((item) => {
    const group = item._parentGroupItem;
    return (group?.type === "LayoutItem") ? group : null;
  }).filter(Boolean));

  const groups = Array.from(groupsSet);

  if (groups?.length > 1) {
    console.warn('More than one group found!');
  }
  
  return (groups?.length) ? groups[0] : null;
}

async function init() {
  if (editorFrame.value) {
    console.log('editor config:', editorConfig.value);
    await initDesignEditor(
      editorFrame.value,
      productDefinition.value as string[],
      editorConfig.value
    );
    emitter.on(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN, async () => {
      return await saveProduct();
    });
    emitter.on(E_EDITOR_CUSTOM_EVENT.GET_PRODUCT_ITEMS, async () => {
      return await getProductItems();
    });
    emitter.on(E_EDITOR_CUSTOM_EVENT.REMOVE_ITEMS_GROUP, async () => {
      const group = await getItemsGroup();
      if (!group) return;
      const query = `#${group?.id}`;
      await commandManagerRef.value?.execute("deleteItems", {
        query,
        force: true, 
      });
      console.warn(`Item has been removed! Query: ${query}`);
    });
    if (!isRestore.value) {
      emitter.emit(E_EDITOR_CUSTOM_EVENT.REMOVE_ITEMS_GROUP);
    }
  } else {
    console.error('Editor frame is not available.');
  }
}

if (!iframeApiLoadStatus) {
  console.error(`Injector iframeApiLoadStatus is ${iframeApiLoadStatus}`);
}

watch(iframeApiLoadStatus!, async (status) => {
  if (status === 'loaded' && !checkLoad) {
    checkLoad = true;
    await init();
  }
}, { immediate: true, deep: true });
</script>

<style scoped lang="scss">
.editor-frame-container {
  position: relative;
  width: 100%;
  height: 100%;
}
#editorFrame {
  border: none;
}
</style>